import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { SiteItem } from '../model/site.model';
import { SpaceService } from './space.service';
import { BusinessRolesService } from './business-roles.service';
import { ContactSubscriptionService } from './contact-subscriptions.service';
import { UserRole } from '../model/business-role.model';
import { ContactSubscription, SubscriptionTable } from '../model/subscription-table.model';
import { Organization, SpaceGroup } from '../model/organization-space-group.model';
import { ActivatedRoute } from '@angular/router';

const ROLE_TO_SUB = ['DR_GENERAL_USER', 'DR_ENERGY_USER', 'IRISH_MARKET_USER', 'DR_FINANCIAL_USER'];
const TABLE_SUBSCRIPTIONS = [
  'NOTIFY_SMS',
  'NOTIFY_PHONE',
  'NOTIFY_EMAIL',
  'NOTIFY_PUSH',
  'OFFER_EMAIL',
  'PERFORMANCE_EMAIL',
  'COACHING_SMS',
  'COACHING_PHONE',
  'COACHING_EMAIL',
  'COACHING_PUSH',
  'PAYMENT_EMAIL',
  'ONBOARDING_EMAIL',
  'ONBOARDING_PUSH',
  'ALERT_RULE_EMAIL',
];
const DISPATCH_SUBSCRIPTIONS = ['NOTIFY_SMS', 'NOTIFY_PHONE', 'NOTIFY_EMAIL', 'NOTIFY_PUSH'];
enum COMMUNICATION_TYPES {
  sms = 'NOTIFY_SMS',
  voice = 'NOTIFY_PHONE',
  email = 'NOTIFY_EMAIL',
  push = 'NOTIFY_PUSH',
  offer = 'OFFER_EMAIL',
  performance = 'PERFORMANCE_EMAIL',
  coachingSms = 'COACHING_SMS',
  coachingVoice = 'COACHING_PHONE',
  coachingEmail = 'COACHING_EMAIL',
  coachingPush = 'COACHING_PUSH',
  paymentEmail = 'PAYMENT_EMAIL',
  remindersEmail = 'ONBOARDING_EMAIL',
  remindersPush = 'ONBOARDING_PUSH',
  alertRuleEmail = 'ALERT_RULE_EMAIL',
}

@Injectable()
export class SubscriptionTableService {
  loadingSites$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loadingTable$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loadingData$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private accessibleSites$: Subject<{ accessibleSites: SiteItem[]; userId: string }> = new Subject<{
    accessibleSites: SiteItem[];
    userId: string;
  }>();
  subscriptionTableData$: BehaviorSubject<SubscriptionTable[]> = new BehaviorSubject<SubscriptionTable[]>([]);
  hasOnlyDRFinancialRole$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  userRoles: UserRole[];
  disableSubData = false;
  hasChanges$ = new BehaviorSubject<boolean>(false);
  previousSubscriptions: SubscriptionTable[] = [];

  constructor(
    private spaceService: SpaceService,
    private businessRolesService: BusinessRolesService,
    private contactSubscriptionService: ContactSubscriptionService,
    private route: ActivatedRoute,
  ) {
    this.businessRolesService.userRoles$.subscribe((userRoles) => {
      const { roles, userId } = userRoles;
      if (roles && userId) {
        this.getListOfAccessibleSites(roles, userId);
      }
    });

    combineLatest([this.contactSubscriptionService.contactSubscriptions$, this.accessibleSites$]).subscribe(
      ([subs, userSites]) => {
        if (this.disableSubData) {
          return;
        }
        const { subscriptions, userId: subsUserId } = subs;
        const { accessibleSites, userId: rolesUserId } = userSites;
        if (subscriptions && accessibleSites && subsUserId === rolesUserId) {
          this.buildSubscriptionTableData(accessibleSites, subscriptions);
        }
      },
    );

    combineLatest([
      this.businessRolesService.loading$,
      this.contactSubscriptionService.loading$,
      this.loadingSites$,
      this.loadingTable$,
    ]).subscribe(([loadingRoles, lodaingSubs, loadingSites, buildingTable]) => {
      this.loadingData$.next(loadingRoles || lodaingSubs || loadingSites || buildingTable);
    });

    this.route.params.subscribe((params) => {
      const userId = params['userId'];
      if (userId) {
        this.subscriptionTableData$.next([]);
        this.previousSubscriptions = [];
      }
    });
  }

  private async getListOfAccessibleSites(userRoles: UserRole[], userId: string) {
    this.userRoles = [...userRoles];
    this.hasOnlyDRFinancialRole$.next(this.hasOnlyDRFinancialRole());
    this.loadingSites$.next(true);
    const entitiesWithRoles = userRoles
      .filter((role) => ROLE_TO_SUB.includes(role.businessRole))
      .map((role) => role.entityIds)
      .flat();

    // Initialize set of table sites
    const subscriptionTableSites = new Set<SiteItem>();

    const sitesWithRoles = this.getListOfEntities(entitiesWithRoles, 'Site') as SiteItem[];

    // Process sites
    sitesWithRoles.forEach((site) => subscriptionTableSites.add(site));

    const orgsWithRoles = this.getListOfEntities(entitiesWithRoles, 'Organization') as Organization[];

    let allFlattenMapOfSpaces = new Map();

    // Process organizations
    for (const org of orgsWithRoles) {
      const flattenMapOfSpaces = await this.spaceService.getFlattenedMapOfTypes(
        ['spacegroup', 'site'],
        'ORGANIZATION',
        org.id,
      );
      allFlattenMapOfSpaces = new Map([...flattenMapOfSpaces, ...allFlattenMapOfSpaces]);
      const orgSites = Array.from(flattenMapOfSpaces.values()).filter(
        (space) => space.space_type == 'Site' && space.type == 'Site',
      );
      orgSites.forEach((site) => subscriptionTableSites.add(site));
    }

    const spaceGroupsWhitRoles = this.getListOfEntities(entitiesWithRoles, 'SpaceGroup') as SpaceGroup[];
    // Process space groups
    for (const spaceGroup of spaceGroupsWhitRoles) {
      if (!allFlattenMapOfSpaces.has(spaceGroup.id)) {
        const spaceGroupHierarchy = await this.spaceService.getHierarchyBySpaceGroupId(spaceGroup.id);
        const spaceGroupSites = this.extractSitesFromHierarchy(spaceGroupHierarchy, 'Site');
        spaceGroupSites.forEach((site) => subscriptionTableSites.add(site));
      }
    }

    // Process subscriptions

    const allAccessibleSites = subscriptionTableSites.size > 0 ? Array.from(subscriptionTableSites) : [];
    this.loadingSites$.next(false);
    this.accessibleSites$.next({ accessibleSites: allAccessibleSites, userId });
  }

  getListOfEntities(
    entityListWithRoles: (SiteItem | SpaceGroup | Organization)[],
    type: string,
  ): (SiteItem | SpaceGroup | Organization)[] {
    return entityListWithRoles.filter((entity) => entity.spaceType === type);
  }

  extractSitesFromHierarchy(entityHierarchy: any, type: string): SiteItem[] {
    const list: SiteItem[] = [];
    if (!entityHierarchy || !entityHierarchy.children || entityHierarchy.children.length <= 0) {
      return list;
    }

    entityHierarchy.children.forEach((entity) => {
      if (entity.spaceType === type) {
        list.push(entity);
      }
      if (entity.children && entity.children.length > 0) {
        const listTypes = this.extractSitesFromHierarchy(entity, type);
        list.push(...listTypes);
      }
    });
    return list;
  }

  private hasOnlyDRFinancialRole(): boolean {
    if (!this.userRoles || this.userRoles.length === 0) {
      return false;
    }

    const subscriptionRoles = this.userRoles.filter((role) => ROLE_TO_SUB.includes(role.businessRole));
    const hasDRGeneralRole = subscriptionRoles.some((role) => role.businessRole === 'DR_GENERAL_USER');

    return (
      !hasDRGeneralRole &&
      subscriptionRoles.length > 0 &&
      subscriptionRoles.every((role) => role.businessRole === 'DR_FINANCIAL_USER')
    );
  }

  async buildSubscriptionTableData(accessibleSites: SiteItem[], subscriptions: ContactSubscription[]) {
    this.loadingTable$.next(true);
    const subscriptionsSitesIds = new Set(
      subscriptions
        .filter(
          ({ communicationName, entityType }) =>
            TABLE_SUBSCRIPTIONS.includes(communicationName) && entityType.toLowerCase() === 'site',
        )
        .map(({ entityId }) => entityId),
    );

    const accessibleSitesIds = new Set(accessibleSites.map((site) => site.id));
    for (const siteId of subscriptionsSitesIds) {
      if (!accessibleSitesIds.has(siteId)) {
        const site = await this.spaceService.getSite(siteId);
        accessibleSites.push(site);
      }
    }

    const siteMap = this.initializeSiteMap(accessibleSites);

    let notifySubscriptions = subscriptions.filter((sub) => TABLE_SUBSCRIPTIONS.includes(sub.communicationName));

    if (this.hasOnlyDRFinancialRole()) {
      notifySubscriptions = notifySubscriptions.filter(
        (sub) => !DISPATCH_SUBSCRIPTIONS.includes(sub.communicationName),
      );
    }

    this.applySubscriptionsToSiteMap(siteMap, notifySubscriptions);

    const sortedSubscriptionTable = this.prepareSubscriptionTable(siteMap);

    this.previousSubscriptions = this.subscriptionTableData$.getValue();

    this.subscriptionTableData$.next(sortedSubscriptionTable);

    this.loadingTable$.next(false);
  }

  private initializeSiteMap(finalListOfSetOfSites: SiteItem[]): Map<string, any> {
    const siteMap = new Map<string, any>();
    finalListOfSetOfSites.forEach((site: SiteItem) => {
      if (site) {
        siteMap.set(site.id, {
          tag: site.id,
          subscriptionId: null,
          name: site.displayLabel || site.name,
          sms: false,
          push: false,
          voice: false,
          email: false,
          offer: false,
          performance: false,
          coachingEmail: false,
          coachingVoice: false,
          coachingPush: false,
          coachingSms: false,
          paymentEmail: false,
          remindersEmail: false,
          remindersPush: false,
          alertRuleEmail: false,
        });
      }
    });
    return siteMap;
  }

  private applySubscriptionsToSiteMap(siteMap: Map<string, any>, subscriptions: ContactSubscription[]): void {
    const communicationMap = {
      [COMMUNICATION_TYPES.email]: 'email',
      [COMMUNICATION_TYPES.voice]: 'voice',
      [COMMUNICATION_TYPES.sms]: 'sms',
      [COMMUNICATION_TYPES.push]: 'push',
      [COMMUNICATION_TYPES.offer]: 'offer',
      [COMMUNICATION_TYPES.performance]: 'performance',
      [COMMUNICATION_TYPES.coachingSms]: 'coachingSms',
      [COMMUNICATION_TYPES.coachingVoice]: 'coachingVoice',
      [COMMUNICATION_TYPES.coachingEmail]: 'coachingEmail',
      [COMMUNICATION_TYPES.coachingPush]: 'coachingPush',
      [COMMUNICATION_TYPES.paymentEmail]: 'paymentEmail',
      [COMMUNICATION_TYPES.remindersEmail]: 'remindersEmail',
      [COMMUNICATION_TYPES.remindersPush]: 'remindersPush',
      [COMMUNICATION_TYPES.alertRuleEmail]: 'alertRuleEmail',
    };

    subscriptions.forEach(({ entityId, entityType, communicationName, subscriptionId }: ContactSubscription) => {
      const matchingSite = siteMap.get(entityId);
      if (matchingSite && matchingSite.subscriptionId === null) {
        matchingSite.entityId = entityId;
        matchingSite.entityType = entityType;
        const key = communicationMap[communicationName];
        if (key) {
          matchingSite[`${key}SubscriptionId`] = subscriptionId;
          matchingSite[key] = true;
        }
      }
    });
  }

  private prepareSubscriptionTable(siteMap: Map<string, any>): SubscriptionTable[] {
    const subscriptionTable = Array.from(siteMap.values());
    return this.sortSubscriptionTable(subscriptionTable);
  }

  sortSubscriptionTable(newSub: SubscriptionTable[]): SubscriptionTable[] {
    const withSubscriptionId = newSub.filter(
      (sub) =>
        sub.emailSubscriptionId ||
        sub.voiceSubscriptionId ||
        sub.smsSubscriptionId ||
        sub.pushSubscriptionId ||
        sub.offerSubscriptionId ||
        sub.performanceSubscriptionId ||
        sub.coachingEmailSubscriptionId ||
        sub.coachingVoiceSubscriptionId ||
        sub.coachingSmsSubscriptionId ||
        sub.coachingPushSubscriptionId ||
        sub.paymentEmailSubscriptionId ||
        sub.remindersEmailSubscriptionId ||
        sub.remindersPushSubscriptionId ||
        sub.alertRuleEmailSubscriptionId,
    );
    const withoutSubscriptionId = newSub.filter(
      (sub) =>
        !(
          sub.emailSubscriptionId ||
          sub.voiceSubscriptionId ||
          sub.smsSubscriptionId ||
          sub.pushSubscriptionId ||
          sub.offerSubscriptionId ||
          sub.performanceSubscriptionId ||
          sub.coachingEmailSubscriptionId ||
          sub.coachingVoiceSubscriptionId ||
          sub.coachingSmsSubscriptionId ||
          sub.coachingPushSubscriptionId ||
          sub.paymentEmailSubscriptionId ||
          sub.remindersEmailSubscriptionId ||
          sub.remindersPushSubscriptionId ||
          sub.alertRuleEmailSubscriptionId
        ),
    );

    withSubscriptionId.sort((a, b) => a.name.localeCompare(b.name));
    withoutSubscriptionId.sort((a, b) => a.name.localeCompare(b.name));

    return (newSub = [...withSubscriptionId, ...withoutSubscriptionId]);
  }

  resetAndDisableSubscriptionData() {
    this.disableSubData = true;
    this.subscriptionTableData$.next([]);
  }

  enableSubscriptionData() {
    this.disableSubData = false;
  }
}
